<script setup lang="ts">
import ButtonClose from '@/components/atoms/ButtonClose.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import { ref, computed } from 'vue'
import { defineResponseHandlers } from '@/libs/utilities/graphql'
import {
  useIssueLoginLinkUrlFromCsvMutation,
  type IssueLoginLinkUrlFromCsvMutation,
} from './SelfGenerationId.generated'
import { useAlert } from '@/libs/alertPortal'
import { parse as parseCSV } from 'papaparse'

const headerColumn = 'userId'

const { mutate } = useIssueLoginLinkUrlFromCsvMutation()

const readCSV = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = event => {
      if (!(event.target instanceof FileReader)) {
        return
      }

      const csv = event.target.result
      resolve(csv as string)
    }

    reader.onerror = event => {
      reject(event)
    }

    reader.readAsText(file)
  })
}

const alert = useAlert()

type CSV = {
  body: string
  name: string
}

const selectedFile = ref<CSV | null>(null)

const selectedFileRowLength = computed(() => {
  const lines = selectedFile.value?.body.split(/\r\n|\n/) ?? []
  const rows = lines.map(line => line.trim()).filter(line => line !== '')
  const header = 1

  return rows.length - header
})

const selectFile = async (event: Event) => {
  if (!(event.target instanceof HTMLInputElement)) {
    return
  }

  const [file] = Array.from(event.target.files || [])
  if (!file) {
    return
  }

  const csv = await readCSV(file)

  selectedFile.value = {
    body: csv,
    name: file.name,
  }
}

const hasUserIdColumnInCSV = computed(() => {
  if (!selectedFile.value) {
    return []
  }

  try {
    parseCSV(selectedFile.value.body, {
      header: true,
      transformHeader(header) {
        if (header !== headerColumn) {
          throw new Error('ヘッダーが不正です')
        }

        return header
      },
    })

    return true
  } catch {
    return false
  }
})

const submit = async () => {
  if (!selectedFile.value) {
    return
  }

  const res = await mutate({
    input: {
      csv: selectedFile.value.body,
    },
  })

  if (res?.errors && res.errors.length > 0) {
    alert.error('エラーが発生しました')
  }

  if (!res?.data) {
    return
  }

  defineResponseHandlers<
    IssueLoginLinkUrlFromCsvMutation['issueLoginLinkURLFromCSV'],
    void
  >({
    IssueLoginLinkURLFromCSVErrorProviderNotFoundPayload: () => {
      alert.error('権限がありません')
    },
    IssueLoginLinkURLFromCSVSuccessPayload: payload => {
      const link = document.createElement('a')
      link.href = URL.createObjectURL(
        new Blob([payload.csv], { type: 'text/csv' })
      )
      link.download = 'ログインURL.csv'
      link.click()
    },
  })(res.data.issueLoginLinkURLFromCSV)
}
</script>

<template>
  <section>
    <div :class="$style.box">
      <div :class="$style.header">
        <p :class="$style.title">選択したファイル</p>
        <label :class="$style.addFileInputLabel"
          >ファイルを追加する
          <input
            type="file"
            multiple
            accept="text/csv"
            @change="selectFile"
            :class="$style.addFileInput"
          />
        </label>
      </div>
      <div :class="$style.fileListWrapper">
        <div v-if="selectedFile">
          <div :class="$style.file">
            <span>・{{ selectedFile.name }}</span>
            <ButtonClose @click="() => (selectedFile = null)" />
          </div>
          <p :class="$style.selected__error" v-if="!hasUserIdColumnInCSV">
            {{ headerColumn }}カラムが必要です
          </p>
          <p v-else :class="$style.userCount">
            合計 {{ selectedFileRowLength }}人
          </p>
        </div>
        <p v-else>未選択</p>
      </div>
    </div>

    <FlatButton
      :class="$style.submit"
      variant="primary"
      size="medium"
      :disabled="!selectedFile || !hasUserIdColumnInCSV"
      @click="submit"
    >
      アップロードしてURLを取得
    </FlatButton>
  </section>
</template>

<style module lang="scss">
.fileSelector {
  display: flex;
  width: 500px;
  height: 100px;

  &--placeholder {
    justify-content: center;
    align-items: center;
    background-color: $color-surface-tertiary;
    color: $color-text-and-object-low-emphasis;
    font-size: 18px;
    text-align: center;
  }

  input {
    display: none;
  }
}

.submit {
  margin-top: 24px;
}

.selected {
  display: flex;
  justify-content: space-between;
  width: 100%;
  border: 1px solid $color-border-low-emphasis;
  border-radius: $border-radius-rounder;
  padding: 16px;

  &__error {
    color: $color-caution-accent;
    font-weight: 700;
  }

  &__content {
    p:nth-child(n + 2) {
      margin-top: 4px;
    }
  }
}

.box {
  border: 1px solid $color-border-low-emphasis;
}

.header {
  display: flex;
  justify-content: space-between;
  background-color: $color-border-low-emphasis;
  padding: 8px 16px;
}

.title {
  font-weight: bold;
}

.addFileInputLabel {
  cursor: pointer;
}

.addFileInput {
  position: absolute !important;
  height: 1px;
  width: 1px;
  overflow: hidden;
  clip: rect(1px, 1px, 1px, 1px);
}

.fileListWrapper {
  padding: 16px;
}

.file {
  display: flex;
  align-items: center;
  gap: 8px;
}

.userCount {
  margin-top: 16px;
}
</style>
